import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Card, Col, Row, Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Container } from "reactstrap";
import Loader from '../../../components/common/Loader';
import AudienceTable from "../../../components/modal/audience/AudienceTable";
import BreadCrumb from '../../../components/common/BreadCrumb';
import AudienceCreate from './AudienceCreate/AudienceCreate';
import AudienceCompareModal from '../../../components/modal/audience/AudienceCompare';
import ReportCreateModal from '../../../components/modal/audience/ReportCreateModal';
import AudienceCsvDownloadModal from '../../../components/modal/audience/AudienceCsvDownloadModal';
import AudienceConfigModal from '../../../components/modal/audience/AudienceConfigModal';
import { FaSpinner } from 'react-icons/fa';
import { showLoadingAlert, showSuccessAlert, showErrorAlert } from '../../../components/custom/sweetalertHelper';
import { getVeloxityAudienceGender } from "../../../slices/veloxity/audience/GenderReducer";
import { getVeloxityAudienceGeneration } from "../../../slices/veloxity/audience/GenerationReducer";
import { getVeloxityAudienceLocations } from "../../../slices/veloxity/audience/LocationReducer";
import { getVeloxityAudiencePurchasing } from "../../../slices/veloxity/audience/PurchasingPowerReducer";
import { getVeloxityAudienceInterests } from "../../../slices/veloxity/audience/InterestReducer";
import { getVeloxityAudienceSales } from "../../../slices/veloxity/audience/SalesPredictionReducer";
import { getTopProfileTypes } from '../../../slices/veloxity/audience/TopProfileTypesReducer';
import { getVeloxityAudienceAnalysis } from '../../../slices/veloxity/audience/AnalysisReducer';
import { getAudienceDefinitionConfig } from '../../../slices/veloxity/audience/DefineConfigReducer';
import { getAudienceSelectionConfig } from '../../../slices/veloxity/audience/SelectionConfigReducer';
import LottieAnimation from '../../../assets/animation/LottieAnimation';


const AudienceMainPage = ({ filteredData, handleDetailClick, onReCalculetedClick, updateLoading, deleteAudienceClick }) => {
  const dispatch = useDispatch();
  const [selectedAudiences, setSelectedAudiences] = useState([]);
  const [modal, setModal] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAnimation, setShowAnimation] = useState(false);

  // Compare
  const [compareData, setCompareData] = useState(null);
  const [compareModal, setCompareModal] = useState(false);
  const toggleCompareModal = () => setCompareModal(!compareModal);
  const [selectedAudienceObjects, setSelectedAudienceObjects] = useState([]);
  const allAudiencesSucceeded = selectedAudienceObjects.every(audience => audience.status === "SUCCEEDED");
  const isCompareButtonDisabled = selectedCount < 2 || selectedCount > 3 || !allAudiencesSucceeded;

  // Report 
  const [isReportCreateModalOpen, setIsReportCreateModalOpen] = useState(false);
  const [selectedAudience, setSelectedAudience] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen(prevState => !prevState);
  const toggleReportCreateModal = () => setIsReportCreateModalOpen(!isReportCreateModalOpen);
  const isReportDownloadButtonVisible = () => selectedCount === 1 && selectedAudience?.report?.status === "SUCCEEDED";
  const isReportCalculatingButtonVisible = () => selectedCount === 1 && ["SCHEDULED", "CALCULATING"].includes(selectedAudience?.report?.status) && "SUCCEEDED" === selectedAudience?.status;
  const isReportCalculateButtonVisible = () => selectedCount === 1 && !["SCHEDULED", "CALCULATING", "SUCCEEDED"].includes(selectedAudience?.report?.status) && "SUCCEEDED" === selectedAudience?.status;
  const isReportDefaultReportButton = () => selectedCount !== 1 || ("SUCCEEDED" !== selectedAudience?.status && selectedCount === 1);

  // CSV
  const [isCsvCreateModalOpen, setIsCsvCreateModalOpen] = useState(false);
  const toggleCsvCreateModal = () => setIsCsvCreateModalOpen(!isCsvCreateModalOpen);
  const isCsvDownloadButtonVisible = () => selectedCount === 1 && selectedAudience?.csv?.status === "SUCCEEDED";
  const isCsvCalculatingButtonVisible = () => selectedCount === 1 && ["SCHEDULED", "CALCULATING"].includes(selectedAudience?.csv?.status);
  const isCsvCalculateButtonVisible = () => selectedCount === 1 && null == selectedAudience?.csv?.status && "SUCCEEDED" === selectedAudience?.status;
  const isCsvDefaultReportButton = () => selectedCount !== 1 || ("SUCCEEDED" !== selectedAudience?.status && selectedCount === 1);

  //Permission
  const subscriptions = useSelector(state => state.getPermissionsList.subscriptions);
  const hasReportPermission = subscriptions?.some(sub => sub.subscriptionType === "REPORT");
  const hasCsvPermission = subscriptions?.some(sub => sub.subscriptionType === "CSV");

  // Config 
  const [configModal, setConfigModal] = useState(false);
  const [configData, setConfigData] = useState({});

  const fetchAudienceConfig = async (audience) => {
    if (!audience) return null;
    const { audienceId, tag } = audience;
    try {
      if (tag === 'AIMEE') {
        const response = await dispatch(getAudienceDefinitionConfig({ audienceId }));
        setConfigData(response.payload);
        setConfigModal(true);
        return response.payload;
      } else {
        const response = await dispatch(getAudienceSelectionConfig({ audienceId }));
        setConfigData(response.payload);
        setConfigModal(true);
        return response.payload;
      }
    } catch (error) {
      console.error('Error fetching audience config:', error);
      return null;
    }
  };

  useEffect(() => {
    setSelectedAudience(filteredData.find(select => select?.audienceId === selectedAudiences[0]));
  }, [selectedAudiences]);

  useEffect(() => {
    const selectedObjects = filteredData.filter(data => selectedAudiences.includes(data.audienceId));
    setSelectedAudienceObjects(selectedObjects);
  }, [selectedAudiences]);

  const handleShowAlert = (message) => {
    setAlertMessage(message);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 6000);
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const handleSelectionChange = (selectedIds) => {
    setSelectedAudiences(selectedIds);
    setSelectedCount(selectedIds.length);
  };

  const handleReportDownloadReport = (format) => {
    if (selectedAudience && selectedAudience.report && selectedAudience.report.status === "SUCCEEDED") {
      let downloadUrl = '';
      switch (format) {
        case 'word':
          downloadUrl = selectedAudience.report.cdnURL.wordFileUrl;
          break;
        case 'powerpoint':
          downloadUrl = selectedAudience.report.cdnURL.powerPointFileUrl;
          break;
        case 'pdf':
          downloadUrl = selectedAudience.report.cdnURL.pdfFileUrl;
          break;
        default:
          showErrorAlert('Error!', 'Invalid format selected.');
          return;
      }
      showLoadingAlert('Please wait...', 'Preparing your download.');
      window.open(downloadUrl, '_blank');
      showSuccessAlert('Success!', '');
    } else {
      showErrorAlert('Error!', 'Report is not available for download.');
    }
  };

  const handleCsvDownloadReport = () => {
    if (selectedAudience && selectedAudience.csv.status === "SUCCEEDED") {
      showLoadingAlert('Please wait...', 'Preparing your download.');
      window.open(selectedAudience.csv.cdnUrl, '_blank');
      showSuccessAlert('Success!', '');
    } else {
      showErrorAlert('Error!', 'Report is not available for download.');
    }
  };

  const onCompareClick = async () => {
    setIsLoading(true);
    await handleCompareAudiences(selectedAudiences);
    setIsLoading(false);
    toggleCompareModal();
  };

  const hasFeature = (feature) => {
    const audienceSubscription = subscriptions?.find(sub => sub.subscriptionType === "AUDIENCE");
    return audienceSubscription?.featureFlags?.[feature] === true;
  };

  const handleCompareAudiences = async (selectedAudienceIds) => {
    setIsLoading(true);
    const enrichedAudienceData = selectedAudienceIds.map(audienceId => {
      const audienceInfo = sortedData.find(audience => audience.audienceId === audienceId);
      return {
        clientId: audienceId,
        name: audienceInfo?.name,
        audienceSize: audienceInfo?.audienceSize,
      };
    });

    const audienceDataResults = await Promise.all(enrichedAudienceData.map(async (audience) => {
      const apiCalls = [
        dispatch(getVeloxityAudienceGeneration({ clientId: audience.clientId })),
        dispatch(getVeloxityAudienceLocations({ clientId: audience.clientId })),
        dispatch(getVeloxityAudiencePurchasing({ clientId: audience.clientId })),
        dispatch(getVeloxityAudienceInterests({ clientId: audience.clientId })),
        dispatch(getTopProfileTypes({ clientId: audience.clientId })),
        dispatch(getVeloxityAudienceAnalysis({ clientId: audience.clientId })),
        dispatch(getVeloxityAudienceGender({ clientId: audience.clientId }))
      ];

      // Sales API çağrısını yetki kontrolüyle yapalım
      let salesData = null;
      if (hasFeature("analytic::sales_prediction")) {
        const salesResponse = await dispatch(getVeloxityAudienceSales({ clientId: audience.clientId }));
        salesData = salesResponse.payload;
      }

      const [
        generation,
        locations,
        purchasing,
        interests,
        topProfileTypes,
        analysis,
        gender
      ] = await Promise.all(apiCalls);

      // Tüm verileri fullData içinde toplayalım
      const fullData = {
        clientId: audience.clientId,
        name: audience.name,
        audienceSize: audience.audienceSize,
        generation: generation.payload,
        locations: locations.payload,
        purchasing: purchasing.payload,
        interests: interests.payload,
        topProfileTypes: topProfileTypes.payload,
        analysis: analysis.payload,
        gender: gender.payload
      };

      // Sales verisini sadece yetki varsa ekleyelim
      if (hasFeature("analytic::sales_prediction")) {
        fullData.sales = salesData;
      }

      return {
        audienceName: audience.name,
        fullData: fullData
      };
    }));

    setCompareData(audienceDataResults);
    setIsLoading(false);
  };

  const toggle = () => setModal(!modal);

  const sortedData = [...filteredData].sort((a, b) => {
    const dateA = new Date(a.updateDate);
    const dateB = new Date(b.updateDate);
    return dateB - dateA;
  });

  const handleLoadingAnimation = async (isLoading) => {
    setShowAnimation(isLoading);
  };

  const moduleTypeOptions = selectedAudienceObjects.length > 0 && selectedAudienceObjects[0].csv && selectedAudienceObjects[0].csv.moduleTypes
    ? selectedAudienceObjects[0].csv.moduleTypes.map(type => ({ value: type, label: type }))
    : [];

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Audience Analytics" pageTitle="Customer Analytics" />
          <Row>
            <Card
              className="p-3"
              style={{
                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                marginBottom: "30px",
                marginLeft: "1%",
                width: "98%",
                height: "90%"
              }}
            >
              <Row>
                <Col lg={6} className="d-flex">
                  <div className="d-flex flex-column align-items-center gap-2 m-lg-3">
                    <Button style={{ maxWidth: "190px" }} color="primary" className="flex-shrink-1" onClick={toggle}>
                      Create Audience
                    </Button>
                    <AudienceCreate isOpen={modal} toggle={toggle} updateLoading={updateLoading} previewLoading={handleLoadingAnimation} />
                  </div>
                </Col>
                <Col lg={6} className="col-sm-auto ms-auto d-flex align-items-center justify-content-end">
                  <div className="d-flex flex-column align-items-center gap-2 m-lg-3" data-bs-toogle="tooltip" data-bs-placement="top" title="2 or 3 audiences must be selected to make a comparison.">
                    <Button style={{ maxWidth: "190px" }} className="flex-shrink-1" onClick={onCompareClick} disabled={isCompareButtonDisabled}>
                      Compare Audience
                    </Button>
                  </div>
                  {hasReportPermission && (
                    <div className="d-flex flex-column align-items-center gap-2 m-lg-3">
                      {isReportDownloadButtonVisible() && (
                        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} direction="down" group>
                          <DropdownToggle caret color="secondary">
                            Download AI Report
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem onClick={() => handleReportDownloadReport('word')}>
                              <i className="mdi mdi-file-word-outline icon-md" /> Word
                            </DropdownItem>
                            <DropdownItem onClick={() => handleReportDownloadReport('powerpoint')}>
                              <i className="mdi mdi-file-powerpoint icon-md" /> PowerPoint
                            </DropdownItem>
                            <DropdownItem onClick={() => handleReportDownloadReport('pdf')}>
                              <i className="mdi mdi-file-pdf-box icon-md" /> PDF
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      )}
                      {isReportCalculatingButtonVisible() && (
                        <Button style={{ maxWidth: "190px" }} disabled>
                          <FaSpinner className="icon-spin" /> Calculating
                        </Button>
                      )}
                      {isReportCalculateButtonVisible() && (
                        <Button style={{ maxWidth: "190px" }} onClick={toggleReportCreateModal}>
                          Generate AI Report
                        </Button>
                      )}
                      {isReportDefaultReportButton() && (
                        <Button style={{ maxWidth: "190px" }} disabled>
                          Generate AI Report
                        </Button>
                      )}
                    </div>
                  )}

                  {hasCsvPermission && (
                    <div className="d-flex flex-column align-items-center gap-2 m-lg-3">
                      {isCsvDownloadButtonVisible() && (
                        <Button style={{ maxWidth: "190px" }} onClick={toggleCsvCreateModal}>
                          Download Data File
                        </Button>
                      )}
                      {isCsvCalculatingButtonVisible() && (
                        <Button style={{ maxWidth: "190px" }} disabled>
                          <FaSpinner className="icon-spin" /> Calculating
                        </Button>
                      )}
                      {isCsvCalculateButtonVisible() && (
                        <Button style={{ maxWidth: "190px" }} onClick={toggleCsvCreateModal}>
                          Generate DATA File
                        </Button>
                      )}
                      {isCsvDefaultReportButton() && (
                        <Button style={{ maxWidth: "190px" }} disabled>
                          Download Data File
                        </Button>
                      )}
                    </div>
                  )}

                </Col>
              </Row>
            </Card>
          </Row>
          {isLoading ?
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '40vh'
            }}>
              <Loader />
            </div>
            : (
              <AudienceTable data={sortedData} onDetailClick={handleDetailClick} onReCalculetedClick={onReCalculetedClick} onSelectionChange={handleSelectionChange} onShowAlert={handleShowAlert} deleteAudienceClick={deleteAudienceClick} fetchAudienceConfig={fetchAudienceConfig} />
            )}
          <AudienceCompareModal 
            isOpen={compareModal} 
            toggle={toggleCompareModal} 
            compareData={compareData} 
            isLoading={isLoading}
            subscriptions={subscriptions}
          />
          <ReportCreateModal isOpen={isReportCreateModalOpen} toggle={toggleReportCreateModal} selectedAudience={selectedAudiences} updateLoading={updateLoading} />
          <AudienceCsvDownloadModal isOpen={isCsvCreateModalOpen} toggle={toggleCsvCreateModal} selectedAudience={selectedAudiences} updateLoading={updateLoading} moduleTypeOptions={moduleTypeOptions} />
          <AudienceConfigModal isOpen={configModal} toggle={() => setConfigModal(!configModal)} configData={configData} />
        </Container>
      </div>
      {showAlert && (
        <div className="alert alert-info alert-dismissible alert-label-icon rounded-label fade show material-shadow" role="alert" style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 9999,
        }}>
          <i className="ri-airplay-line label-icon"></i><strong>Info</strong> - {alertMessage}
          <button type="button" className="btn-close" onClick={handleCloseAlert} aria-label="Close"></button>
        </div>
      )}
      {showAnimation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 9998,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <LottieAnimation />
        </div>
      )}
    </>
  );
};

export default AudienceMainPage;
